<template>
    <div class="field">
        <label class="label" v-if="label">{{ label }}</label>
        <div class="control">
            <input
                class="input"
                type="datetime-local"
                :value="localValue"
                @input="onDateTimeInput"
                :max="maxDateTime"
                :min="minDateTime"
            />
        </div>
        <div class="is-flex is-align-items-center mt-1 is-size-7 has-text-grey">
            <template v-if="!editingTz">
                <span>{{ currentTimezone }}</span>
                <button
                    type="button"
                    class="button is-small is-text p-0 ml-2"
                    @click="editingTz = true"
                    :aria-label="`Change time zone (currently ${currentTimezone})`"
                >change</button>
            </template>
            <div v-else class="select is-small">
                <select :value="currentTimezone" @change="onTimezoneChange">
                    <option v-for="tz in zoneList" :key="tz" :value="tz">{{ tz }}</option>
                </select>
            </div>
        </div>
        <p class="help has-text-danger" v-if="error">{{ error }}</p>
    </div>
</template>

<script setup>
// Native datetime-local + IANA zone select. The input value is interpreted as
// wall-clock time in `currentTimezone`; v-model exposes a UTC ISO string so
// existing callers don't need to know about the zone, and parents that *do*
// care can bind v-model:timezone to record it on the API call.
import { computed, ref, watch } from 'vue';
import {
  getDeviceTimezone,
  isoToWallClock,
  listSupportedTimezones,
  wallClockToISO,
} from '../utils/time';
import { useBlocksStore } from '../stores/blocks';

const props = defineProps({
    modelValue: {
        type: [String, Date],
        default: null
    },
    timezone: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    error: {
        type: String,
        default: ''
    },
    maxDate: {
        type: [String, Date],
        default: null
    },
    minDate: {
        type: [String, Date],
        default: null
    }
});

const emit = defineEmits(['update:modelValue', 'update:timezone']);

const store = useBlocksStore();

const editingTz = ref(false);
const zoneList = listSupportedTimezones();

const defaultTimezone = () => store.getUserSetting('home_timezone', '') || getDeviceTimezone();

// Internal tz: prefer prop, then user setting, then device.
const internalTimezone = ref(props.timezone || defaultTimezone());
watch(() => props.timezone, (val) => {
    if (val && val !== internalTimezone.value) internalTimezone.value = val;
});

const currentTimezone = computed(() => internalTimezone.value || defaultTimezone());

const localValue = computed(() => isoToWallClock(props.modelValue, currentTimezone.value));

const maxDateTime = computed(() => isoToWallClock(props.maxDate, currentTimezone.value));
const minDateTime = computed(() => isoToWallClock(props.minDate, currentTimezone.value));

const onDateTimeInput = (event) => {
    const value = event.target.value;
    if (value) {
        emit('update:modelValue', wallClockToISO(value, currentTimezone.value));
    } else {
        emit('update:modelValue', null);
    }
};

const onTimezoneChange = (event) => {
    const tz = event.target.value;
    internalTimezone.value = tz;
    emit('update:timezone', tz);
    editingTz.value = false;
    // Re-emit the same instant so callers that ignore update:timezone still
    // get the equivalent UTC, and callers that read it see consistent values.
    if (props.modelValue) {
        emit('update:modelValue', new Date(props.modelValue).toISOString());
    }
};
</script>
